import React, { useContext } from 'react'
import { useSelector } from 'react-redux'
import { ExpandableText } from 'transfix-ui/components/ExpandableText'
import { MapMarker } from 'transfix-ui/components/MapMarker'
import { Tooltip } from 'transfix-ui/components/Tooltip'
import { IFormattedStop } from 'types/shipment'
import { FeatureFlagsContext } from 'utils/context/FeatureFlagsContext'
import useUserDetails from 'utils/hooks/useUserDetails'
import { selectShipmentDetails } from 'apps/booking/utils/selectors/shipment'
import { trackingRequirementFullDescription } from 'apps/booking/utils/trackingRequirements'
import { Bullet } from 'components/atoms'
import { DropTrailerIcon, InfoIcon } from 'transfix-icons'
import { Card, Tag, TAG_STYLE, TAG_SIZE } from 'transfix-ui'
import { capitalize } from 'utils/capitalize'

const LoadDetail = ({ stop }: { stop: IFormattedStop }) => {
  const {
    appointmentDate,
    appointmentTime,
    facility: { city, companyName, detail, shipperFacilityUuid, stateCode, zipcode },
    isDropTrailer,
    nextStopMiles,
    requirements,
    specialInstruction,
    startAtTimezone,
    stopSequence,
    stopType,
  } = stop

  return (
    <>
      {nextStopMiles && <p className='text-caption-semibold mt4'>{nextStopMiles} miles to</p>}
      <div className='flex items-center'>
        <div>
          <MapMarker sequence={stopSequence} type={stopType} />
        </div>
        <div className='ma4'>
          <p className='b pv0'>
            {city}, {stateCode}&nbsp;
            <span className='text-secondary'>{zipcode}</span>
            {isDropTrailer && <DropTrailerIcon size='medium' className='ml1' />}
          </p>
          <p className='pv0'>
            {appointmentDate} - {appointmentTime} {startAtTimezone}
          </p>
          {detail?.uuid && (
            <p className='text-accent b pv0'>
              <a
                href={`${__ROOT__}/facilities/${detail.uuid}/shipper_facilities/${shipperFacilityUuid}`}
                target='_blank'
                rel='noopener noreferrer'>
                {companyName}
              </a>
            </p>
          )}
          {requirements?.map((requirement: string) => (
            <div className='mt2' key={requirement}>
              <Tag label={requirement} size={TAG_SIZE.xsmall} tagStyle={TAG_STYLE.info} />
            </div>
          ))}
        </div>
      </div>
      {specialInstruction && (
        <div className='coolGray1 pa4 mt2'>
          <strong>{capitalize(stopType)} Notes</strong>
          <p className='pv0 word-wrap'>{specialInstruction}</p>
        </div>
      )}
    </>
  )
}

const ShipmentInfoCard = () => {
  const {
    carrierSpecialInstructions,
    ceilingRate,
    ceilingRatePerMile,
    commodity,
    dropTrailer,
    equipmentDetails,
    equipmentName,
    id,
    listRate,
    listRatePerMile,
    shipperTrackingSegment,
    specialInstruction,
    stops,
    totalMiles,
    totalPalletCount,
    totalWeight,
    trackingRequired,
  } = useSelector(selectShipmentDetails)
  const showTracking = shipperTrackingSegment?.id || trackingRequired

  const { getFlag } = useContext(FeatureFlagsContext)
  const { enabled: maskCmtRates } = getFlag('maskCmtRates')
  const { userPermissions } = useUserDetails()
  const { canViewCmTargetRates } = userPermissions || {}
  const displayCmtRates = !maskCmtRates || canViewCmTargetRates

  return (
    <Card>
      <strong>Shipment #{id}</strong>

      {/* Load Details */}
      <section className='mt4 flex'>
        <div className='flex flex-column flex-grow-1'>
          <div>
            <strong>{capitalize(commodity)}</strong>
          </div>
          <div>
            <strong>{equipmentName}</strong>
            <Bullet />
            {totalMiles} mi
          </div>
          <div>
            <strong>{totalWeight} lbs</strong>
            <Bullet />
            {totalPalletCount} pallets
          </div>
          <div>{equipmentDetails}</div>
        </div>
        <div className='flex flex-grow-1'>
          <div className='flex flex-column text-secondary'>
            {displayCmtRates && <div>CM Target</div>}
            <div>List Rate</div>
          </div>
          <div className='ml4'>
            {displayCmtRates && (
              <div>
                {ceilingRate} &nbsp;
                <span className='text-secondary'>{ceilingRatePerMile}</span>
              </div>
            )}
            <div>
              {listRate} &nbsp;
              <span className='text-secondary'>{listRatePerMile}</span>
            </div>
          </div>
        </div>
      </section>
      {dropTrailer && (
        <section className='mv4'>
          <Tag
            label='Drop Trailer Required'
            size={TAG_SIZE.small}
            tagStyle={TAG_STYLE.info}
            textStyle='mr2 ml1'
            icon={<DropTrailerIcon verticalAlign='baseline' />}
          />
        </section>
      )}
      {showTracking && (
        <section className='mv4'>
          {shipperTrackingSegment?.id ? (
            <>
              <div className='b'>Tracking Status</div>
              <div>
                {shipperTrackingSegment.title}
                <Tooltip
                  position='bottom'
                  id='shipper-tracking-segment'
                  trigger={
                    <span>
                      <InfoIcon className='ml2' />
                    </span>
                  }>
                  <div className='shipment-info__tooltip'>
                    <b>{shipperTrackingSegment.title}</b>
                    <p>{trackingRequirementFullDescription.get(shipperTrackingSegment.level)}</p>
                  </div>
                </Tooltip>
              </div>
            </>
          ) : (
            <Tag label='Tracking Required' size={TAG_SIZE.small} tagStyle={TAG_STYLE.warning} />
          )}
        </section>
      )}
      {specialInstruction && (
        <div className='coolGray1 pa4 mt2'>
          <strong>Special Instructions</strong>
          <p className='pv0 word-wrap'>{specialInstruction}</p>
        </div>
      )}
      <hr />

      {/* Shipment Details + Notes */}
      <section className='mv4'>
        {stops?.map((stop: IFormattedStop) => (
          <LoadDetail key={stop.id} stop={stop} />
        ))}
      </section>
      <hr />

      {/* Carrier Instructions */}
      {carrierSpecialInstructions && (
        <section>
          <ExpandableText
            text={carrierSpecialInstructions}
            label='Carrier Instructions'
            tall
            id='carrier-special-instructions'
          />
        </section>
      )}
    </Card>
  )
}

export default ShipmentInfoCard
